import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Package, Plus, Trash2, Loader2, Search, Camera, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { fetchParts, createPart, deletePart, uploadImage } from "@/lib/dashboardApi";

const CATEGORIES = ["General", "Indoor Air Quality", "HVAC", "HVAC Service", "Changeout / Equipment", "Heat Pump", "Gas Furnace", "Condenser", "Air Handler", "Thermostat", "Ductwork", "Refrigerant", "Filters", "Materials"];
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-800 focus:outline-none focus:border-[#F97316]";
const EMPTY = { name: "", sku: "", description: "", standard_price: "", agreement_price: "", category: "General", vendor: "", image_url: "" };

const PartsCatalog = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [lightbox, setLightbox] = useState("");
  const [cat, setCat] = useState("All");
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setParts(await fetchParts()); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file); set("image_url", url); toast.success("Image uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Image upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Part name is required"); return; }
    setSaving(true);
    try {
      const std = Number(form.standard_price) || 0;
      await createPart({ ...form, standard_price: std, agreement_price: Number(form.agreement_price) || 0, cost: std, quantity: form.quantity || "" });
      toast.success("Part added");
      setForm(EMPTY); load();
    } catch { toast.error("Failed to add part"); }
    finally { setSaving(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the catalog?`)) return;
    try { await deletePart(p.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return parts.filter((p) => {
      if (cat !== "All" && (p.category || "General") !== cat) return false;
      if (!s) return true;
      return `${p.name} ${p.sku} ${p.description} ${p.category} ${p.vendor}`.toLowerCase().includes(s);
    });
  }, [parts, q, cat]);
  const catList = useMemo(() => { const set = new Set(parts.map((p) => p.category || "General")); return ["All", ...Array.from(set).sort()]; }, [parts]);
  const grouped = useMemo(() => { const g = {}; filtered.forEach((p) => { (g[p.category || "General"] = g[p.category || "General"] || []).push(p); }); return g; }, [filtered]);
  return (
    <div className="space-y-6" data-testid="parts-catalog-page">
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Package className="w-5 h-5 text-[#F97316]" /><h3 className="text-slate-900 font-semibold text-lg">Add a Part</h3></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Part Name *</label><input data-testid="part-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. APCO-X Replacement Bulb" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Part / Model #</label><input value={form.sku} onChange={(e) => set("sku", e.target.value)} className={inputCls} placeholder="e.g. UVL010000" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Category</label><select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        </div>
        <div className="mt-3"><label className="block text-xs font-semibold text-slate-500 mb-1">Description</label><input value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} placeholder="What the part is / where it's used" /></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Standard Price ($)</label><input data-testid="part-standard-price" type="number" step="0.01" value={form.standard_price} onChange={(e) => set("standard_price", e.target.value)} className={inputCls} placeholder="0.00" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Agreement Price ($)</label><input data-testid="part-agreement-price" type="number" step="0.01" value={form.agreement_price} onChange={(e) => set("agreement_price", e.target.value)} className={inputCls} placeholder="0.00" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Supplier / Vendor</label><input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} className={inputCls} placeholder="e.g. Johnstone Supply" /></div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 rounded-lg font-medium cursor-pointer text-sm" data-testid="part-image-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {form.image_url ? "Change Image" : "Add Image"}
            <input ref={fileRef} type="file" accept="image/*" onChange={onImageFile} className="hidden" data-testid="part-image-input" />
          </label>
          <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className={`${inputCls} flex-1 min-w-[180px]`} placeholder="…or paste an image URL" />
          {form.image_url && (
            <div className="relative shrink-0">
              <img src={form.image_url} alt="preview" className="h-14 w-14 object-cover rounded-lg border border-slate-200" />
              <button type="button" onClick={() => set("image_url", "")} className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-300 p-0.5 text-slate-500 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <button data-testid="part-save-btn" onClick={save} disabled={saving} className="ml-auto flex items-center gap-2 bg-[#F97316] hover:bg-[#ea6a0c] text-white px-5 py-2.5 rounded-lg font-semibold disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Part
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="text-slate-900 font-semibold text-lg">Parts &amp; Equipment <span className="text-slate-400 text-sm font-normal">({parts.length})</span></h3>
          <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><input value={q} onChange={(e) => setQ(e.target.value)} data-testid="parts-search" placeholder="Type to search parts…" className="bg-white border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-[#F97316]" /></div>
        </div>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="parts-category-filter">
          {catList.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cat-pill-${c}`} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${cat === c ? "bg-[#F97316] border-[#F97316] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-[#F97316]"}`}>
              {c}{c !== "All" && <span className="opacity-70"> ({parts.filter((p) => (p.category || "General") === c).length})</span>}
            </button>
          ))}
        </div>
        {loading ? (
          <p className="text-slate-400 text-sm py-10 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-400 text-sm py-10 text-center">{parts.length === 0 ? "No parts yet — add one above." : "No parts match your filter."}</p>
        ) : (
          <div className="space-y-7">
            {Object.keys(grouped).sort().map((category) => (
              <div key={category} data-testid={`cat-section-${category}`}>
                <h4 className="text-slate-800 font-semibold text-sm uppercase tracking-wide border-b border-slate-200 pb-2 mb-3">{category} <span className="text-slate-400">· {grouped[category].length}</span></h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {grouped[category].map((p) => (
                    <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow group" data-testid={`part-card-${p.id}`}>
                      <button onClick={() => p.image_url && setLightbox(p.image_url)} className="block w-full h-32 bg-slate-100">
                        {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-slate-300" /></div>}
                      </button>
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-slate-900 font-semibold text-sm leading-snug">{p.name}</p>
                          <button onClick={() => remove(p)} className="text-slate-300 hover:text-red-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {p.sku && <p className="text-slate-500 text-xs font-mono mt-0.5">{p.sku}</p>}
                        {p.description && <p className="text-slate-500 text-xs mt-1 line-clamp-2">{p.description}</p>}
                        <div className="flex items-end justify-between mt-2 pt-2 border-t border-slate-100">
                          <div>
                            <p className="text-slate-900 font-bold">{money(p.standard_price || p.cost)}</p>
                            {p.agreement_price > 0 && <p className="text-[#F97316] text-xs">Agr {money(p.agreement_price)}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setLightbox("")} data-testid="parts-lightbox">
          <img src={lightbox} alt="part full" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
};
export default PartsCatalog;
